import { notify, TabPanel } from '@/components';
import {
  UpdatePricingMarkupDefaultsArgs,
  useGetPricingSettingsMatrixQuery,
  useUpdateMarkupDefaultsMutation,
} from '@/redux';
import { Col, Divider, Row } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuSelect } from './MenuSelect';
import { PricingSettingsDefaultsForm } from './PricingSettingsDefaultsForm';
import { PricingSettingsListPanel } from './PricingSettingsListPanel';
import { PricingMarkupDefaultsDto } from '@/core';

interface Props {
  communityId: string;
}

function useUpdateDefaultMarkups() {
  const [update] = useUpdateMarkupDefaultsMutation();
  return useCallback(
    (value: UpdatePricingMarkupDefaultsArgs) =>
      update(value)
        .unwrap()
        .then(() => notify.success.t('pricing.settings.saved')),
    [update],
  );
}

const DEFAULT_MARKUPS_DATA: PricingMarkupDefaultsDto = {
  construction: { value: 0, isOverridden: true },
  resident: { value: 0, isOverridden: true },
  salesTax: { value: 0, isOverridden: true },
  applyTo: [],
};
export function PricingSettingsListTabPanel(props: Props) {
  const { communityId } = props;
  const { t } = useTranslation();
  const [menuId, setMenuId] = useState<string>();
  const { data } = useGetPricingSettingsMatrixQuery({ communityId, menuId });
  const { data: allMenusData } = useGetPricingSettingsMatrixQuery({
    communityId,
  });
  const value: UpdatePricingMarkupDefaultsArgs = useMemo(
    () => ({
      communityId,
      constructionMarkup:
        data?.id && data?.defaultMarkups.construction?.isOverridden
          ? data?.defaultMarkups.construction.value
          : undefined,
      residentMarkup:
        data?.id && data?.defaultMarkups.resident?.isOverridden
          ? data?.defaultMarkups.resident.value
          : undefined,
      salesTax:
        data?.id && data?.defaultMarkups.salesTax?.isOverridden
          ? data?.defaultMarkups.salesTax?.value
          : undefined,
      applyTo: data?.id ? data?.defaultMarkups?.applyTo : undefined,
      menuId,
    }),
    [
      communityId,
      data?.defaultMarkups?.applyTo,
      data?.defaultMarkups.construction,
      data?.defaultMarkups.resident,
      data?.defaultMarkups.salesTax,
      data?.id,
      menuId,
    ],
  );
  const handleSubmit = useUpdateDefaultMarkups();

  return (
    <TabPanel>
      <TabPanel.Title>{t('pricing.settings.title')}</TabPanel.Title>
      <TabPanel.Body>
        {data && (
          <>
            <Row className="mb-2">
              <Col span={16}>
                <Row>
                  <Col span={13}>
                    <MenuSelect
                      communityId={communityId}
                      menuId={menuId}
                      setMenuId={setMenuId}
                    />
                  </Col>
                </Row>
              </Col>
              <Divider />
            </Row>
            <Row>
              <PricingSettingsDefaultsForm
                value={value}
                onSubmit={handleSubmit}
                autoSubmit
                initialMarkups={data?.defaultMarkups}
                defaultMarkups={
                  menuId ? allMenusData?.defaultMarkups : DEFAULT_MARKUPS_DATA
                }
              />
            </Row>
          </>
        )}
        <PricingSettingsListPanel
          className="mt-4"
          communityId={communityId}
          menuId={menuId}
        />
      </TabPanel.Body>
    </TabPanel>
  );
}
